import { Editor, MultiRootEditor } from 'ckeditor5';

import {
  QBox,
  QFlex,
  QSpacer,
  QStack,
  useCurrentUser,
  useDisclosure,
  useToastProvider,
} from '@qualio/ui-components';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { useMultiRootEditor } from '@ckeditor/ckeditor5-react';
import { ResourceData } from '@qualio/search-qri';
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useSearchParams } from 'react-router-dom';
import { multiRootEditorReadyHandler } from '../../../../config/handlers/EditorEventsHandlers';
import { getEditorConfigMultiRoot } from '../../../../config/MultiRootEditorConfig';
import { useAnnotationHighlighting } from '../../../../hooks/AnnotationHighlighting';
import { useElementIsRendered } from '../../../../hooks/ElementRenderedCheck';
import {
  hasEditorFinishedEditing,
  isOwner,
} from '../../../../util/CurrentUser';
import { getRevisionHistoryEditClass } from '../../../../util/EditModeUtils';
import {
  buildAttachmentsErrorMessage,
  buildToastDescription,
  isAttachmentError,
} from '../../../../util/ErrorManagementUtils';
import {
  reflowSidebar,
  refreshDisplayMode,
} from '../../../../util/SidebarUtils';
import { saveContentFromMultiRootEditor } from '../../../reducers/DocumentReducer';
import { BackToTop } from '../../BackToTop';
import { ToolbarWrapper } from '../../ToolbarWrapper/ToolbarWrapper';
import {
  DocumentOverviewContext,
  EditorStatus,
  EditorStatusContext,
} from '../Context';
import { ReusableContentModal } from '../DocumentVariantRenderer/ReusableContentModal';
import { HTMLViewer } from '../HTMLViewer';
import { canUserEditDocument, canUserSuggestOnDocument } from '../RenderLogic';
import { CommentButtons } from './CommentButtons';
import { CommentSidebar } from './CommentSidebar';
import { QMultiRootEditorContext } from './Context';
import { OutlineSidebar } from './OutlineSidebar';
import styles from './QMultiRootEditor.module.css';
import { QMultiRootEditorLayout } from './QMultiRootEditorLayout';
import { RevisionHistorySidebar } from './RevisionHistorySidebar';

export const QMultiRootEditor = ({
  handleAttachmentClick,
  handleSessionDisconnectionEvent,
}: {
  handleAttachmentClick: (attachmentId: string) => void;
  handleSessionDisconnectionEvent: (didReceive: boolean) => void;
}) => {
  const { t } = useTranslation();
  const { setDocEditorStatus } = useContext(EditorStatusContext);
  const { handleHighlighting } = useAnnotationHighlighting();

  const [searchParams] = useSearchParams();
  const targetId = searchParams.get('targetId') ?? undefined;
  const location = useLocation();
  const editorContainerRef = useRef<HTMLDivElement | null>(null);
  const editorInstanceRef = useRef<MultiRootEditor | null>(null);

  const editorRevisionHistoryRef = useRef(null);
  const editorRevisionHistoryEditorRef = useRef(null);
  const [isLayoutReady, setIsLayoutReady] = useState(false);

  const [, sidebarElement] = useElementIsRendered('#commentsSidebar');
  const [, docPresenceListContainer] = useElementIsRendered(
    '.doc-overview-presence-list',
  );

  const [, docRevisionHistorySidebar] = useElementIsRendered(
    '#commentsSidebar-revision-history',
  );

  const [, editorToolbarRef] = useElementIsRendered('#editor-toolbar');

  useEffect(() => {
    setIsLayoutReady(true);

    return () => setIsLayoutReady(false);
  }, []);

  useEffect(() => {
    setDocEditorStatus(EditorStatus.CONNECTING);

    return () => {
      setDocEditorStatus(EditorStatus.STABLE);
    };
  }, [setDocEditorStatus]);

  const {
    qualioDocument,
    commentsPermission,
    users,
    tags,
    collaborationToken,
  } = useContext(DocumentOverviewContext);
  const currentUser = useCurrentUser();
  const {
    additionalPremiumPlugins,
    usePagination,
    useRevisionHistory,
    importFromWord,
    editorBundleEnabled,
  } = useFlags();

  const { showToast } = useToastProvider();
  const aiEnabled = currentUser.company.ai_enabled;

  const multiRootDefaultDataAll = {} as any;
  const multiRootDefaultRootsAttributesAll = {} as any;
  const showSectionTitles = qualioDocument.text_sections_titles_visible;

  multiRootDefaultDataAll['table-of-contents'] =
    qualioDocument.table_of_contents ?? '';
  multiRootDefaultRootsAttributesAll['table-of-contents'] = {
    order: 1,
    isHeader: false,
    rootName: 'table-of-contents',
  };

  qualioDocument.sections.forEach((section, index) => {
    if (showSectionTitles) {
      multiRootDefaultDataAll[`${section.id}-header`] =
        `<h1 >${section.position}. ${section.title} </h1>`;
      multiRootDefaultRootsAttributesAll[`${section.id}-header`] = {
        order: section.position + 2,
        isHeader: true,
        rootName: section.id + '-header',
      };
    }

    multiRootDefaultDataAll[`${section.id}`] = section.content;
    multiRootDefaultRootsAttributesAll[`${section.id}`] = {
      order: section.position + 3,
      isHeader: false,
      rootName: section.id,
    };
  });

  const rootsContent = multiRootDefaultDataAll;
  const rootsAttributes = multiRootDefaultRootsAttributesAll;

  const { setEditor } = useContext(QMultiRootEditorContext);

  const handleAutoSave = useCallback(
    async (editor: Editor): Promise<void> => {
      if (commentsPermission <= 1) {
        return;
      }

      let currentDocumentData: Record<string, string>;
      if (useRevisionHistory) {
        const revisionTracker = editor.plugins.get('RevisionTracker') as any;
        const currentRevision = revisionTracker.currentRevision;
        await revisionTracker.update();
        currentDocumentData =
          await revisionTracker.getRevisionDocumentData(currentRevision);
      } else {
        currentDocumentData = (editor as MultiRootEditor).getFullData();
      }

      await saveContentFromMultiRootEditor(
        qualioDocument,
        currentDocumentData,
        editor,
        (e) => {
          setDocEditorStatus(EditorStatus.ERROR);
          const errorMsg = isAttachmentError(e)
            ? buildAttachmentsErrorMessage(e, editor, qualioDocument)
            : e;
          showToast({
            'data-cy': 'document-editor-save-error-toast',
            id: 'document-editor-save-error',
            status: 'error',
            title: t('notifications.errorSavingSectionContent'),
            description: buildToastDescription(errorMsg),
            replace: true,
          });
          return Promise.resolve();
        },
        () => {
          // Explicitly set SAVED so ERROR state is cleared after a successful retry
          setDocEditorStatus(EditorStatus.SAVED);
        },
      );
    },
    // Do not add showToast to this array, it will crash the editor mid render
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [qualioDocument.sections, setDocEditorStatus, commentsPermission],
  );

  const {
    isOpen: isReusableContentModalOpen,
    onOpen: onOpenReusableContentModal,
    onClose: onCloseReusableContentModal,
  } = useDisclosure();

  const editorConfig = useMemo(() => {
    return getEditorConfigMultiRoot({
      companyId: qualioDocument.company_id,
      collaborationToken,
      sidebarConfig: {
        sidebarElement,
        preventScrollOutOfView: false,
      },
      presenceListConfig: {
        containerElement: docPresenceListContainer,
      },
      editorAiEnabled: aiEnabled,
      qualioDocument,
      users,
      tags,
      handleAutoSave,
      commentsPermission,
      collaborationEntityId: qualioDocument.id.toString(),
      collaborationEntityName: 'document',
      documentId: qualioDocument.id,
      documentStatusId: qualioDocument.status_id,
      documentOwner: qualioDocument.owner,
      entityType: 'document',
      editorContainerRef: editorContainerRef,
      editorRevisionHistoryRef: editorRevisionHistoryRef,
      editorRevisionHistoryEditorRef: editorRevisionHistoryEditorRef,
      docRevisionHistorySidebar: docRevisionHistorySidebar,
      editorInstanceRef: editorInstanceRef,
      editorToolbarRef: editorToolbarRef,
      additionalPremiumPlugins,
      usePagination,
      currentUser,
      useRevisionHistory,
      importFromWord,
      attachmentPreview: handleAttachmentClick,
      onOpenReusableContentModal,
      useQualioTableOfContentsPlugin: true,
      editorBundleVersion: editorBundleEnabled
        ? process.env.REACT_APP_EDITOR_BUNDLE_VERSION
        : undefined,
    });
  }, [
    qualioDocument,
    collaborationToken,
    sidebarElement,
    docPresenceListContainer,
    aiEnabled,
    users,
    tags,
    handleAutoSave,
    commentsPermission,
    docRevisionHistorySidebar,
    editorToolbarRef,
    additionalPremiumPlugins,
    usePagination,
    currentUser,
    useRevisionHistory,
    handleAttachmentClick,
    onOpenReusableContentModal,
    importFromWord,
    editorBundleEnabled,
  ]);

  const editorProps = {
    isLayoutReady,
    editor: MultiRootEditor,
    data: rootsContent,
    rootsAttributes,
    config: editorConfig as Record<string, unknown>,
    onError: (error: any) => {
      console.error(error);
    },
    disableTwoWayDataBinding: true,
    disableWatchdog: true,
    onReady: (editor: MultiRootEditor) => {
      editorInstanceRef.current = editor;
      setEditor(editor);
      const roots = editor.getRootsAttributes();
      Object.keys(roots).forEach((key: string) => {
        const root = roots[key];
        const lockId = root.isHeader ? 'header' : 'tableOfContents';
        if (
          root.isHeader ||
          (root.rootName as string) === 'table-of-contents'
        ) {
          editor.disableRoot(root.rootName as string, lockId);
        }
      });

      multiRootEditorReadyHandler(
        editor,
        'documentEditorContent',
        currentUser,
        commentsPermission,
        'documentCommentControl',
        qualioDocument.orientation,
        qualioDocument.status_id,
        setDocEditorStatus,
        handleHighlighting,
        handleAttachmentClick,
        qualioDocument.id,
        handleSessionDisconnectionEvent,
        targetId,
        true,
        hasEditorFinishedEditing(currentUser, qualioDocument),
        isOwner(currentUser, qualioDocument),
      );
    },
  };

  const { editableElements, toolbarElement, attributes, editor } =
    useMultiRootEditor(editorProps);

  const handleInsertReusableContent = (resource: ResourceData) => {
    if (!editor) return;
    onCloseReusableContentModal();
    (editor.plugins.get('ReusableContent') as any).insertReusableContent(
      resource,
    );
  };

  // Sorted elements based on order attribute.
  const sortedElements = editableElements.sort((a, b) => {
    const orderA = Number(attributes[a.props.id]?.order ?? 0);
    const orderB = Number(attributes[b.props.id]?.order ?? 0);
    return orderA - orderB;
  });

  const showToolbar = useMemo(() => {
    return (
      canUserEditDocument(qualioDocument, currentUser) ||
      canUserSuggestOnDocument(qualioDocument, currentUser)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    qualioDocument.status_id,
    qualioDocument.owner.id,
    qualioDocument.audit_groups?.editor_group,
    currentUser.userId,
  ]);

  useEffect(() => {
    reflowSidebar([editor]);
    const dialog = editorInstanceRef.current?.plugins.get('Dialog') as any;
    if (dialog?.isOpen && dialog.id === 'commentsArchive') {
      dialog.hide();
    }
  }, [location, editor]);

  useEffect(() => {
    const tabPanels = document.getElementById('document-overview-tab-panels');
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        if (entry.contentBoxSize) {
          const inlineSize = entries[0].borderBoxSize[0].inlineSize ?? 0;
          refreshDisplayMode({
            instances: [editor],
            windowWidth: inlineSize || undefined,
            breakpointOverride:
              qualioDocument.orientation === 'landscape' ? 1650 : 1400,
          });
        }
      }
    });
    if (tabPanels) {
      observer.observe(tabPanels);
    }
    return () => {
      if (tabPanels) {
        observer.unobserve(tabPanels);
      }
    };
  }, [editor, qualioDocument.orientation]);

  useEffect(() => {
    if (!editor?.plugins.has('PaginationLookup')) {
      return;
    }

    const paginationConfig = editorConfig.pagination;
    if (!paginationConfig) {
      return;
    }

    const paginationLookup = editor.plugins.get('PaginationLookup') as any;
    const pagesContainer = paginationLookup._pagesContainer?.element;

    if (!pagesContainer) {
      return;
    }

    const {
      pageWidth,
      pageHeight,
      pageMargins: { top, bottom },
    } = paginationConfig;

    pagesContainer.style.columnWidth = pageWidth;
    pagesContainer.style.width = pageWidth;
    pagesContainer.style.height = `calc( ${pageHeight} - ${top} - ${bottom} + 0.5mm )`;

    paginationLookup._recalculatePageBreaks?.();
    // editorConfig is intentionally omitted, we only want to react to orientation changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor, qualioDocument.orientation]);

  return (
    <>
      {isReusableContentModalOpen && (
        <ReusableContentModal
          isOpen={isReusableContentModalOpen}
          onClose={onCloseReusableContentModal}
          onSelect={handleInsertReusableContent}
        />
      )}
      {showToolbar && (
        <ToolbarWrapper
          className={`${
            styles['document-overview__toolbar']
          } ${getRevisionHistoryEditClass(qualioDocument)}`}
          id="editor-toolbar"
        >
          {toolbarElement}
        </ToolbarWrapper>
      )}
      {!showToolbar && (
        <QBox style={{ display: 'none' }}>{toolbarElement}</QBox>
      )}
      <QFlex
        flexShrink={1}
        flexGrow={1}
        justifyContent="space-between"
        bg="gray.50"
        border="1px"
        borderTop={showToolbar && Boolean(editor) ? 'none' : '1px'}
        borderRadius={showToolbar && Boolean(editor) ? '0 0 4px 4px' : '4px'}
        borderColor="gray.200"
        className={`${
          styles['document-content-container']
        } ${getRevisionHistoryEditClass(qualioDocument)}`}
        overflowY="hidden"
        overflowX="hidden"
        ref={editorContainerRef}
      >
        <OutlineSidebar
          editor={editor}
          scrollBoundaryRef={editorContainerRef}
          toolbarElementId="editor-toolbar"
          outlineClassSuffix="document"
          sections={qualioDocument.sections}
          sectionTitlesVisible={qualioDocument.text_sections_titles_visible}
          editorName="documentEditorContent"
        />

        <QStack direction="row" id="document-multiroot-container">
          <QMultiRootEditorLayout contentId="documentEditorContent">
            <QBox
              className={`editor-container_classic-editor editor-container_include-annotations ${
                commentsPermission < 2 ? 'non-editable-mode' : ''
              }`}
            >
              <QBox className="editor-container__editor-wrapper">
                <QBox className="editor-container__editor">
                  {!editor && (
                    <HTMLViewer
                      sections={qualioDocument.sections}
                      showSectionTitles={
                        qualioDocument.text_sections_titles_visible
                      }
                      disableCheckBoxes={true}
                      tableOfContents={qualioDocument.table_of_contents}
                    />
                  )}
                  <QBox className={`${editor ? '' : 'ck-hidden'}`}>
                    {sortedElements.map((element, index) => {
                      const shouldRenderFakeHeader =
                        !attributes[element.props.id]?.isHeader &&
                        element.props.id !== 'table-of-contents';

                      return (
                        <QBox
                          key={`editor-root-${element.props.id}`}
                          className="editor-root"
                        >
                          {shouldRenderFakeHeader && (
                            <h1
                              key={`fake-header-${element.props.id}`}
                              style={{
                                height: '0px !important',
                                padding: '0px !important',
                                margin: '0px !important',
                              }}
                              className={'section-heading fake'}
                            >
                              <></>
                            </h1>
                          )}
                          {element}
                        </QBox>
                      );
                    })}
                  </QBox>
                </QBox>
              </QBox>
            </QBox>
          </QMultiRootEditorLayout>
          <CommentButtons id="documentCommentControl" />
        </QStack>
        {/* Sidebars live OUTSIDE of the editor container */}
        <CommentSidebar id="commentsSidebar" />
      </QFlex>
      {useRevisionHistory && (
        <QFlex
          flexShrink={1}
          flexGrow={1}
          justifyContent="space-between"
          bg="gray.50"
          border="1px"
          borderTop={showToolbar ? 'none' : '1px'}
          borderColor="gray.200"
          borderRadius={showToolbar ? '0 0 4px 4px' : '4px'}
          display="none"
          className={`${styles['document-content-container']}`}
          overflowY="hidden"
          overflowX="hidden"
          ref={editorRevisionHistoryRef}
        >
          <QFlex
            direction="column"
            flexShrink={1}
            flexGrow={1}
            className={`${styles['document-toc-container']}`}
            style={{
              minWidth: '0px',
            }}
          >
            <QSpacer height="100%" />
            <BackToTop parentRef={editorContainerRef} />
          </QFlex>

          <QStack
            direction="row"
            id="document-revision-history-container"
            className={`${getRevisionHistoryEditClass(qualioDocument)}`}
          >
            <QMultiRootEditorLayout contentId="documentEditorContent">
              <QBox className="revision-history" ref={editorRevisionHistoryRef}>
                <QBox className="revision-history__wrapper">
                  <QBox
                    className="revision-history__editor"
                    ref={editorRevisionHistoryEditorRef}
                    id="editor-revision-history-editor"
                  />
                </QBox>
              </QBox>
            </QMultiRootEditorLayout>
          </QStack>
          {/* Sidebars live OUTSIDE of the editor container */}
          <RevisionHistorySidebar id="commentsSidebar-revision-history" />
        </QFlex>
      )}
    </>
  );
};
